<template>
  <div class="night-hours-section">
    <div class="night-hours-header">
      <div class="night-hours-header-content">
        <h3 class="night-hours-title">Heures de nuit</h3>
        <p class="night-hours-description">Permet le calcul automatique des majorations et indemnités de nuit.</p>
      </div>
      <AwButton variant="outline" color="secondary" :disabled="!canEdit" @click="canEdit && emit('edit')">
        Modifier
      </AwButton>
    </div>

    <AwSkeleton v-if="loading" class="h-16 w-full rounded-lg" />
    <div v-else class="night-hours-content">
      <div class="night-hours-fields">
        <div class="night-hours-field">
          <div class="night-hours-field-label">Majoration (ponctuel / exceptionnel)</div>
          <div class="night-hours-field-value">
            {{ nightHoursSurchargeTypeLabel(settings) }}
          </div>
        </div>
        <div class="night-hours-field">
          <div class="night-hours-field-label">Plage des horaires de nuit</div>
          <div class="night-hours-field-value">
            {{ nightHoursTimeRangeLabel(settings) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dateFormat } from "@asap/shared/utils/date";
import { translateNightHoursSurchargeType } from "@asap/shared/utils/translate";
import type { NightHoursSurchargeType } from "@asap/shared/utils/supabase.enum";
import type { CompanyRemunerationSettings } from "@asap/shared/schemas/remuneration/company-remuneration-settings";

const TIME_ANCHOR_DATE = "2026-03-11";

defineProps<{
  settings: CompanyRemunerationSettings | null;
  loading?: boolean;
  canEdit?: boolean;
}>();

const emit = defineEmits<{
  edit: [];
}>();

/** Formats a time string (e.g. "22:00:00") to "HHhMM" for display using dateFormat. */
const formatTimeForDisplay = (time: string | null | undefined): string => {
  if (!time) return "-";
  const normalized = time.length === 5 ? `${time}:00` : time;
  const date = new Date(`${TIME_ANCHOR_DATE}T${normalized}`);
  if (Number.isNaN(date.getTime())) return "-";
  return dateFormat(date, { options: { timeStyle: "short" } }).replace(":", "h");
};

const nightHoursSurchargeTypeLabel = (settings: CompanyRemunerationSettings | null): string => {
  if (!settings?.night_hours_enabled || !settings.night_hours_surcharge_type) return "-";
  const key = settings.night_hours_surcharge_type;
  let label: string;
  if (key in translateNightHoursSurchargeType.fr) {
    label = translateNightHoursSurchargeType.fr[key as NightHoursSurchargeType] ?? key;
  } else {
    label = key;
  }
  if (settings.night_hours_surcharge_percent != null) {
    return `${label} (${settings.night_hours_surcharge_percent} %)`;
  }
  return label;
};

const nightHoursTimeRangeLabel = (settings: CompanyRemunerationSettings | null): string => {
  if (!settings?.night_hours_enabled || !settings.night_hours_start || !settings.night_hours_end) {
    return "-";
  }
  return `${formatTimeForDisplay(settings.night_hours_start)} - ${formatTimeForDisplay(settings.night_hours_end)}`;
};
</script>

<style scoped lang="scss">
.night-hours-section {
  margin-top: 24px;
}

.night-hours-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.night-hours-header-content {
  flex: 1;
}

.night-hours-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.night-hours-description {
  font-size: 13px;
  color: var(--gray-2);
  margin: 0;
}

.night-hours-fields {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.night-hours-field {
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  background-color: var(--gray-7);
  border: 1px solid var(--gray-6);
  border-radius: 8px;
}

.night-hours-field-label {
  font-size: 12px;
  color: var(--gray-2);
  margin-bottom: 4px;
}

.night-hours-field-value {
  font-size: 14px;
  font-weight: 600;
}
</style>
